import Auth from "src/Auth";
import { debounce, isEqual } from "lodash";
import { DRAFT_PREFLIGHT_SNAPSHOT_KEY } from "./draftPreflightPersistence";

const buildDraftContentValue = (documentScenes, patch) => {
  const baseValue =
    documentScenes?.value && typeof documentScenes.value === "object" && !Array.isArray(documentScenes.value)
      ? documentScenes.value
      : {};
  const newValue = { ...baseValue, ...patch };
  delete newValue[DRAFT_PREFLIGHT_SNAPSHOT_KEY];
  return newValue;
};

export function buildDraftScenesValue(documentScenes, scenesJson) {
  return buildDraftContentValue(documentScenes, { scenes: scenesJson });
}

export function applyOptimisticDraftScenesEdit({ documentId, scenesJson, getDocumentScenes, setDocumentScenes, saveDocumentScenes }) {
  return applyOptimisticDraftContentEdit({ documentId, patch: { scenes: scenesJson }, getDocumentScenes, setDocumentScenes, saveDocumentScenes });
}

export const applyOptimisticDraftContentEdit = ({ documentId, patch, getDocumentScenes, setDocumentScenes, saveDocumentScenes }) => {
  const currentDocumentScenes = getDocumentScenes(documentId);
  const newValue = buildDraftContentValue(currentDocumentScenes, patch);

  // Preserve updatedAt so the next debounced save includes the optimistic lock token
  const optimisticEntity = { ...(currentDocumentScenes || {}), value: newValue };
  if (currentDocumentScenes?.updatedAt) {
    optimisticEntity.updatedAt = currentDocumentScenes.updatedAt;
  }
  setDocumentScenes(documentId, optimisticEntity);
  saveDocumentScenes(newValue);

  return newValue;
};

export function shouldApplyDraftScenesSaveResponse(currentDocumentScenes, savedValue) {
  const storeValue = currentDocumentScenes?.value;
  if (storeValue === savedValue) return true;
  // Strip draftPreflightSnapshot before comparing — preflight writes add this
  // server-side metadata key to the store, but debounced scene saves never
  // include it (buildDraftScenesValue strips it).  Without this, a concurrent
  // preflight write causes the guard to report false-negative, dropping valid edits.
  const { [DRAFT_PREFLIGHT_SNAPSHOT_KEY]: _a, ...storeContent } = storeValue || {};
  const { [DRAFT_PREFLIGHT_SNAPSHOT_KEY]: _b, ...savedContent } = savedValue || {};
  return isEqual(storeContent, savedContent);
}

// Each Draft instance owns one document's pending and active content writes.
// Metadata writes may advance the server lock token, but never own local edits.
export const createDraftScenesPersistence = ({ documentId, getDocumentScenes, setDocumentScenes, onError, request = Auth.fetchWithIdToken }) => {
  let revision = 0;
  let pending = null;
  let active = null;

  const isCurrent = (edit) => edit.revision === revision && shouldApplyDraftScenesSaveResponse(getDocumentScenes(documentId), edit.value);
  const put = (value, updatedAt) => request("/api/scenes/", {
    method: "PUT",
    documentId,
    value,
    ...(updatedAt ? { updatedAt } : {}),
  });

  const save = async (edit) => {
    let updated;
    try {
      updated = await put(edit.value, getDocumentScenes(documentId)?.updatedAt);
    } catch (error) {
      if (error?.status !== 409) throw error;
      if (!isCurrent(edit)) return;
      const fresh = await request("/api/scenes/", { documentId });
      // A new edit can arrive during the GET as well as during the PUT.
      if (!isCurrent(edit)) return;
      updated = await put(edit.value, fresh.updatedAt);
    }
    if (isCurrent(edit)) {
      const current = getDocumentScenes(documentId);
      if (!current?.updatedAt || !updated?.updatedAt || new Date(updated.updatedAt) >= new Date(current.updatedAt)) {
        setDocumentScenes(documentId, updated);
      }
    }
  };

  const drain = () => {
    if (active) return active;
    active = (async () => {
      while (pending) {
        const edit = pending;
        pending = null;
        try {
          await save(edit);
        } catch (error) {
          // Retain failed work so a later flush cannot silently claim it saved.
          if (edit.revision === revision) {
            pending = edit;
            throw error;
          }
          // Superseded work does not block the latest queued edit.
        }
      }
    })().finally(() => { active = null; });
    return active;
  };

  const debouncedSave = debounce(() => { drain().catch(onError); }, 1000);
  const schedule = (value) => {
    pending = { value, revision: ++revision };
    debouncedSave();
  };
  const flush = async () => {
    // Unlike debounce.flush(), this also awaits writes already in flight and
    // drains edits queued while those requests are awaiting responses.
    do {
      debouncedSave.cancel();
      await drain();
    } while (pending);
  };
  const cancel = () => {
    debouncedSave.cancel();
    revision++;
    pending = null;
  };
  const reconcileMetadata = async (persist) => {
    const startedAtRevision = revision;
    const updated = await persist();
    const current = getDocumentScenes(documentId);
    if (updated && startedAtRevision === revision && shouldApplyDraftScenesSaveResponse(current, updated.value) &&
      (!current?.updatedAt || (updated.updatedAt && new Date(updated.updatedAt) >= new Date(current.updatedAt)))) {
      setDocumentScenes(documentId, updated);
    }
    return updated;
  };

  return { schedule, flush, cancel, reconcileMetadata };
};
